'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale } from 'next-intl';
import { useMemo } from 'react';
import useSWRInfinite from 'swr/infinite';
import { SEARCH_PAGE_LIMIT, SEARCH_PARAM_KEYS, SEARCH_TOP_WORLDS_LIMIT } from '@/constants/search';
import { useConfigsContext } from '@/context/ConfigsContext';
import { useRouteChangeContext } from '@/context/RouteChangeContext';
import { useBrowserLocale } from '@/hooks/useBrowserLocale';
import { useLoadMore } from '@/hooks/useLoadMore';
import { useSearchParamsSchema } from '@/hooks/useSearchParamsSchema';
import { getApiDevice, getApiSort, getSearchContentTypes, mapRoomResults, SearchResultTab } from '@/lib/search';
import { ContentTypeCode, Room, searchRooms } from '@/services/cms';
export type SearchContentType = Exclude<ContentTypeCode, 'templates'>;
type FetcherResult = {
  results: Room[];
  next_cursor: string | undefined;
};
const getKey = (pageIndex: number, previousPageData: FetcherResult | null | undefined, keyword: string | null, tag: string | null, sort: string | null, device: string | null, tab: SearchResultTab | null, locale: string, browserLocale: string, contentType: SearchContentType) => {
  if (!keyword || !browserLocale) return null;
  const params = new URLSearchParams({
    [SEARCH_PARAM_KEYS.KEYWORD]: keyword
  });
  const isTop = !tab || tab === 'top';
  params.set(SEARCH_PARAM_KEYS.TAB, tab || 'top');
  params.set('content_type', contentType);
  if (!isTop && tag) params.set(SEARCH_PARAM_KEYS.GENRE, tag);
  if (!isTop && device && contentType !== 'videos') params.set(SEARCH_PARAM_KEYS.DEVICE, device);
  if (isTop) {
    params.set(SEARCH_PARAM_KEYS.SORT, 'recommend');
  } else if (sort) {
    params.set(SEARCH_PARAM_KEYS.SORT, sort);
  }
  const prefix = contentType === 'videos' ? 'searchVideos' : 'searchWorlds';
  if (pageIndex === 0) return `${prefix}[${locale}|${browserLocale}]?${params.toString()}`;
  if (!previousPageData?.next_cursor) return null;
  params.set('cursor', previousPageData.next_cursor);
  return `${prefix}[${locale}|${browserLocale}]?${params.toString()}`;
};
const createFetcher = (htcToken: string | undefined, locale: string, browserLocale: string) => {
  return (key: string): Promise<FetcherResult | undefined> => {
    const query = key.split('?')[1];
    const urlParams = new URLSearchParams(query);
    const cursor = urlParams.get('cursor') || undefined;
    const keyword = urlParams.get(SEARCH_PARAM_KEYS.KEYWORD) || '';
    const tag = urlParams.get(SEARCH_PARAM_KEYS.GENRE) || undefined;
    const sort = urlParams.get(SEARCH_PARAM_KEYS.SORT) || undefined;
    const device = urlParams.get(SEARCH_PARAM_KEYS.DEVICE) || undefined;
    const tab = urlParams.get(SEARCH_PARAM_KEYS.TAB) || undefined;
    const apiSort = getApiSort(sort, true);
    const apiDevice = getApiDevice(device) || 'desktop';
    const contentType = urlParams.get('content_type') as SearchContentType | undefined;
    const isVideos = contentType === 'videos';
    const requestParams: Parameters<typeof searchRooms>[0] = {
      q: keyword,
      limit: tab === 'top' ? SEARCH_TOP_WORLDS_LIMIT : SEARCH_PAGE_LIMIT,
      cursor,
      ...(!isVideos && tab !== 'top' && {
        device: apiDevice
      }),
      ...(tab !== 'top' && tag && {
        tag
      }),
      sort: tab === 'top' ? 'recommend' : apiSort,
      ...(contentType && {
        content_types: getSearchContentTypes(contentType)
      }),
      site_locale: locale,
      locale: browserLocale
    };
    return searchRooms(requestParams, htcToken, false).then(res => {
      if (!res) return undefined;
      const results = mapRoomResults(res.results?.results || []);
      return {
        results,
        next_cursor: res?.results?.next_cursor
      };
    });
  };
};
export function useWorldsSearch(contentType, enablePagination) {
  const $ = _c(30);
  const {
    keyword,
    genre,
    sort,
    device,
    tab
  } = useSearchParamsSchema();
  const {
    store
  } = useConfigsContext();
  const htcToken = store?.credentials?.htcToken;
  const locale = useLocale();
  const browserLocale = useBrowserLocale();
  const {
    isRouteLoading
  } = useRouteChangeContext();
  let t0;
  if ($[0] !== browserLocale || $[1] !== contentType || $[2] !== device || $[3] !== genre || $[4] !== keyword || $[5] !== locale || $[6] !== sort || $[7] !== tab) {
    t0 = (pageIndex, previousPageData) => getKey(pageIndex, previousPageData, keyword, genre, sort, device, tab, locale, browserLocale, contentType);
    $[0] = browserLocale;
    $[1] = contentType;
    $[2] = device;
    $[3] = genre;
    $[4] = keyword;
    $[5] = locale;
    $[6] = sort;
    $[7] = tab;
    $[8] = t0;
  } else {
    t0 = $[8];
  }
  let t1;
  if ($[9] !== browserLocale || $[10] !== htcToken || $[11] !== locale) {
    t1 = createFetcher(htcToken, locale, browserLocale);
    $[9] = browserLocale;
    $[10] = htcToken;
    $[11] = locale;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  let t2;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      revalidateFirstPage: false
    };
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  const {
    data,
    isLoading,
    isValidating,
    size,
    setSize
  } = useSWRInfinite(t0, t1, t2);
  let t3;
  if ($[14] !== data) {
    t3 = data?.flatMap(_temp).filter(_temp2) || [];
    $[14] = data;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const allItems = t3;
  const hasMore = data?.[data.length - 1]?.next_cursor !== undefined;
  const t4 = enablePagination && hasMore;
  let t5;
  if ($[16] !== isLoading || $[17] !== isValidating || $[18] !== setSize || $[19] !== size || $[20] !== t4) {
    t5 = {
      enabled: t4,
      isLoading,
      isValidating,
      setSize,
      size
    };
    $[16] = isLoading;
    $[17] = isValidating;
    $[18] = setSize;
    $[19] = size;
    $[20] = t4;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const loadMoreRef = useLoadMore(t5);
  const t6 = !browserLocale || isLoading;
  const t7 = enablePagination && hasMore;
  let t8;
  if ($[22] !== allItems || $[23] !== isLoading || $[24] !== isRouteLoading || $[25] !== isValidating || $[26] !== loadMoreRef || $[27] !== t6 || $[28] !== t7) {
    t8 = {
      allItems,
      isPending: t6,
      isLoading,
      isValidating,
      isRouteLoading,
      enableLoadMore: t7,
      loadMoreRef
    };
    $[22] = allItems;
    $[23] = isLoading;
    $[24] = isRouteLoading;
    $[25] = isValidating;
    $[26] = loadMoreRef;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  return t8;
}
function _temp2(room) {
  return room !== undefined;
}
function _temp(page) {
  return page?.results || [];
}